
import {
  Component,
  Vue,
  Prop,
  Watch,
  Emit,
  Inject,
} from "vue-property-decorator";
import { IDocument, IDocumentTranslations } from "@/models/Documents";
import {
  MORTGAGE_DOC_LOKALISE_KEYS,
  AFTER_SALES_DOC_LOKALISE_KEYS,
} from "@/constants";
import HiboFileUploaderModal from "@/components/ui/HiboFileUploaderModal.vue";
import { IMortgageStatus } from "@/models/Mortgages";
import { BUSINESS, AVALON_PROPERTIES_ID } from "@/constants";
import api from "@/api";

@Component({
  components: { HiboFileUploaderModal },
})
export default class DocumentsWidget extends Vue {
  @Inject() readonly curr_business!: string;

  @Prop({ type: [Object, String], required: true })
  protected operationStatus!: IMortgageStatus | string;

  @Prop({ type: Number, required: true })
  protected operationId!: number;

  @Prop({ type: Object, required: true })
  protected document!: IDocument;

  protected loading = true;
  protected uploadUrl = "";
  protected uploaderModal = false;
  protected documentComment = "";
  private saveCommentTimeout: number | null = null;

  protected get user(): any {
    return this.$store.state.auth.user;
  }

  protected get isAvalonProperties(): boolean {
    const targetId = AVALON_PROPERTIES_ID;
    return this.user?.agentId === targetId || this.user?.parentId === targetId;
  }

  protected get isMortgageDocumentVerified(): boolean {
    return !!this.document.verifiedByAgentId;
  }

  protected get checkboxValue(): boolean {
    return this.isMortgageDocumentVerified;
  }

  protected set checkboxValue(value: boolean) {
    if (value) {
      this.validateMortgageDocument();
    }
  }

  protected async validateMortgageDocument(): Promise<void> {
    try {
      await api.request.call({
        method: "PATCH",
        url: `hibo/v1/mortgages/${this.operationId}/documents/${this.document.id}`,
      });

      this.$emit("document-verified", this.document);
    } catch (error) {
      console.error("Error validating documents:", error);
      this.$emit("error", this.$t("documents.validationError"));
    }
  }

  protected debouncedSaveComment(): void {
    if (this.saveCommentTimeout) {
      window.clearTimeout(this.saveCommentTimeout);
    }

    this.saveCommentTimeout = window.setTimeout(() => {
      this.saveDocumentComment();
    }, 2000);
  }

  protected async saveDocumentComment(): Promise<void> {
    const trimmedComment = this.documentComment.trim();
    if (!trimmedComment) return;

    try {
      await api.request.call({
        method: "POST",
        url: `hibo/v1/mortgages/${this.operationId}/documents/${this.document.id}/comments`,
        data: {
          comment: trimmedComment,
          agentId: this.user?.agentId,
        },
      });

      this.$emit("comment-saved", this.document);
    } catch (error) {
      console.error("Error saving document comment:", error);
      this.$emit("error", this.$t("documents.commentError"));
    }
  }

  beforeDestroy() {
    if (this.saveCommentTimeout) {
      window.clearTimeout(this.saveCommentTimeout);
    }
    if (this.documentComment.trim()) {
      this.saveDocumentComment();
    }
  }

  protected getTranslationsById(id: number): IDocumentTranslations | null {
    return (
      MORTGAGE_DOC_LOKALISE_KEYS.find((translate) => translate.id === id) ||
      null
    );
  }

  protected getTranslationsByCode(type: string): IDocumentTranslations | null {
    return (
      AFTER_SALES_DOC_LOKALISE_KEYS.find(
        (translate) => translate.code === type
      ) || null
    );
  }

  protected setDocumentInitialData() {
    if (this.curr_business === BUSINESS.mortgages) {
      if (this.document.documentTypeId)
        this.document.translations =
          this.getTranslationsById(this.document.documentTypeId) || null;
    } else {
      if (this.document.type)
        this.document.translations =
          this.getTranslationsByCode(this.document.type) || null;
    }

    this.$store
      .dispatch(`${this.curr_business}/getUploadDocUrl`, {
        operationId: this.operationId,
        documentId: this.document.id,
      })
      .then((result) => (this.uploadUrl = result));

    this.documentComment = this.document.hiboComment || "";
    this.loading = false;
  }

  @Emit("uploaded")
  protected docsUploaded() {
    this.uploaderModal = false;
  }

  @Watch("document", { immediate: true })
  documentUpdate() {
    this.setDocumentInitialData();
  }

  @Watch("document.comment")
  commentUpdate() {
    if (this.document.hiboComment !== this.documentComment) {
      this.documentComment = this.document.hiboComment || "";
    }
  }
}
